'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Infrastructure-as-Code security programme design',
    description:
      'A structured, organisation-wide IaC security programme built around your specific tooling, cloud providers, and delivery model. Covers policy design, enforcement gates, team training, and ongoing governance, not a one-off scan.',
  },
  {
    tag: '[02]',
    title: 'Policy-as-code framework implementation',
    description:
      'Custom OPA, Sentinel, or Checkov rulesets encoding your organisation\'s security standards. Policies version-controlled, peer-reviewed, and enforced consistently across every pipeline, with clear exception and override processes.',
  },
  {
    tag: '[03]',
    title: 'Supply chain and SBOM programme',
    description:
      'End-to-end software supply chain security: SBOM generation for every build, dependency signing, provenance verification, and a continuous monitoring programme that flags new CVEs against your deployed inventory.',
  },
  {
    tag: '[04]',
    title: 'Secrets and drift governance',
    description:
      'A programme-level approach to secrets management and infrastructure drift, covering tooling selection, workflow integration, alerting, and the organisational processes needed to keep secrets out of code and infrastructure in its defined state.',
  },
];

const outcomes = [
  { metric: 'Pre-apply', label: 'All misconfigurations caught before any environment is affected' },
  { metric: '100%', label: 'IaC coverage across every resource type and provider' },
  { metric: 'Zero', label: 'Secrets committed to version control' },
  { metric: 'Continuous', label: 'Drift detection and automated remediation' },
];

const techStack = [
  { category: 'IaC Platforms', tools: ['Terraform', 'Pulumi', 'CloudFormation', 'Ansible', 'Helm'] },
  { category: 'IaC Security', tools: ['Checkov', 'tfsec', 'Terrascan', 'KICS'] },
  { category: 'Policy-as-Code', tools: ['OPA / Rego', 'Sentinel', 'Custom Checkov rules'] },
  { category: 'Secrets Detection', tools: ['Gitleaks', 'TruffleHog', 'detect-secrets', 'HashiCorp Vault'] },
  { category: 'SBOM & Supply Chain', tools: ['Syft', 'Grype', 'Sigstore / Cosign', 'SLSA'] },
  { category: 'Pipeline Platforms', tools: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'Azure DevOps', 'Tekton'] },
];

const phases = [
  {
    phase: '01',
    title: 'Discovery & current state',
    desc: 'Audit of existing IaC tooling, pipeline configuration, policy coverage, and secrets posture. Identify gaps and prioritise by risk.',
  },
  {
    phase: '02',
    title: 'Programme design',
    desc: 'Design the target-state IaC security programme: policy framework, enforcement gates, SBOM strategy, secrets governance, and team operating model.',
  },
  {
    phase: '03',
    title: 'Implementation & integration',
    desc: 'Hands-on implementation of tooling, policies, and pipeline integrations. Embedded engineers working directly in your repositories and CI/CD systems.',
  },
  {
    phase: '04',
    title: 'Handover & continuous improvement',
    desc: 'Full documentation, team enablement, and a defined process for ongoing policy evolution. Optional continuous support retainer.',
  },
];

const differentiators = [
  { title: 'Programme, not a scan', desc: 'We build a durable, governed IaC security capability, not a point-in-time assessment that gathers dust.' },
  { title: 'Embedded delivery', desc: 'Our engineers work inside your repositories and pipelines, not in a separate workstream.' },
  { title: 'Tooling-agnostic', desc: 'We work with your existing IaC stack, Terraform, Pulumi, CloudFormation, Helm, and select the right security tooling to match.' },
  { title: 'Framework-aligned', desc: 'Programme outputs map directly to SOC 2, ISO 27001, DORA, and CIS Controls requirements for infrastructure security.' },
];

export default function IaCProgrammePage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-100px', right: '10%', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '450px', height: '450px', bottom: '-100px', left: '-50px', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Programmes</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">IaC Security Programme</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [IaC Security Programme]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Infrastructure security<br />
              <span className="text-[#80edfa]">built as a programme, not a scan.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              A structured, organisation-wide IaC security programme covering policy-as-code, supply chain security, secrets governance, and drift detection, embedded into your pipelines and your team.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-3xl lg:text-4xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-sm text-[#555] leading-snug">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Use cases</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What we deliver</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement phases */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Engagement model</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">How the programme runs</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {phases?.map((p, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[{p?.phase}]</p>
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{p?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{p?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Tooling</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">Technology stack</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {techStack?.map((t, i) => (
              <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.6rem] text-[#555] uppercase tracking-widest mb-4">{t?.category}</p>
                <div className="flex flex-wrap gap-2">
                  {t?.tools?.map((tool) => (
                    <span key={tool} className="font-mono-brand text-[0.65rem] text-[#aaa] border border-[#222] px-3 py-1.5 uppercase tracking-wide">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Why TechSonar</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium">What makes this different</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators?.map((d, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-4" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-2">{d?.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{d?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#f5f5f8] font-medium mb-6">
              Ready to build a durable IaC security programme?
            </h2>
            <p className="text-[#666] text-lg mb-8 leading-relaxed">
              Talk to us about your current IaC posture, tooling, and delivery model. We will scope a programme that fits your organisation and delivers measurable outcomes.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services" className="btn-secondary">View all services</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
